import type { VoidFunctionComponent } from 'react';

import { FETCHER_DROPDOWN_OPTIONS } from '../../../../shared/fetchers';
import { useOrganizationPackageLabels } from '../../../hooks/api/packageLabels';
import FilterCard from '../../generic/Filter/FilterCard';
import FormLabel from '../../generic/Forms/FormLabel';
import usePackageManagers from '../hooks/usePackageManagers';
import type { Fetcher } from '../../../../shared/fetchers';
import type { AnyFilter } from '../../../../shared/organizationPackages/filters';
import type { SelectOption } from '../../../types';

import PackageLabelsFilter from './PackageLabelsFilter';
import PackageLocatorFilter from './PackageLocatorFilter';
import PackageManagerFilter from './PackageManager';

type Props = {
  areFetchersLoading: boolean;
  fetcherOptions: Array<SelectOption<Fetcher>>;
  selectedFetchers: Array<SelectOption<Fetcher>>;
  arePackageLabelsLoading: boolean;
  packageLabelOptions: string[];
  selectedPackageLabels: string[];
  selectedLocators: SelectOption[];
  setFilters: (filters: AnyFilter) => void;
};

/**
 * PackageInventoryFiltersProject has filters related to filtering
 * packages by package manager (fetcher), labels, and locator.
 */
const PackageInventoryFiltersPackages: VoidFunctionComponent<Props> = ({
  areFetchersLoading,
  fetcherOptions,
  selectedFetchers,
  arePackageLabelsLoading,
  packageLabelOptions,
  selectedPackageLabels,
  selectedLocators,
  setFilters,
}) => (
  <FilterCard id="package-inventory-package-filters" title="Package">
    <FormLabel label="Package manager">
      <PackageManagerFilter
        isLoading={areFetchersLoading}
        fetcherOptions={fetcherOptions}
        selectedFetchers={selectedFetchers}
        setFilters={setFilters}
      />
    </FormLabel>
    <FormLabel label="Labels">
      <PackageLabelsFilter
        isLoading={arePackageLabelsLoading}
        packageLabelOptions={packageLabelOptions}
        selectedPackageLabels={selectedPackageLabels}
        setFilters={setFilters}
      />
    </FormLabel>
    <FormLabel label="Locator">
      <PackageLocatorFilter locators={selectedLocators} setFilters={setFilters} />
    </FormLabel>
  </FilterCard>
);

/**
 * Stateful container for the package filters as they are on the main /packages page.
 */
type ContainerProps = {
  filters: AnyFilter;
  setFilters: (filters: AnyFilter) => void;
};

const PackageInventoryFiltersPackagesContainer: VoidFunctionComponent<ContainerProps> = ({ filters, setFilters }) => {
  const { fetchers: fetcherOptions, isLoading: areFetchersLoading } = usePackageManagers();
  const selectedFetchers = FETCHER_DROPDOWN_OPTIONS.filter(({ value }) => filters.fetchers?.includes(value));

  // Load org package labels with the useOrganizationPackageLabels hook.
  const { data: packageLabelsResponse, isLoading: arePackageLabelsLoading } = useOrganizationPackageLabels();
  const packageLabels = packageLabelsResponse?.packageLabels ?? [];
  const packageLabelOptions = packageLabels.map((packageLabel) => packageLabel.name);
  const selectedPackageLabels = filters.packageLabels ?? [];
  const selectedLocators = (filters.locators ?? []).map((locator) => ({ value: locator, label: locator }));

  return (
    <PackageInventoryFiltersPackages
      areFetchersLoading={areFetchersLoading}
      fetcherOptions={fetcherOptions}
      selectedFetchers={selectedFetchers}
      arePackageLabelsLoading={arePackageLabelsLoading}
      packageLabelOptions={packageLabelOptions}
      selectedPackageLabels={selectedPackageLabels}
      selectedLocators={selectedLocators}
      setFilters={setFilters}
    />
  );
};

export default PackageInventoryFiltersPackagesContainer;
